import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { useLocalStorage, useInterval } from 'usehooks-ts';
import RequestOTP from './RequestOTP/RequestOTP';
import VerifyOTP from './VerifyOTP/VerifyOTP';
import axios from '../../../axios-base';
import { tokenStorage } from '../../../auth/tokenStorage';
import useJuryStore from '../../../store/juryStore';

/**
 * JuryAuth - Main component for jury authentication flow
 * Handles OTP request and verification against the real backend API.
 */
function JuryAuth(props) {
    const location = useLocation();
    const history = useHistory();

    // Extract initial email from query parameter if present
    const queryEmail = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('email') || '';
    }, [location.search]);

    const [step, setStep] = useState('request'); // 'request' or 'verify'
    const [email, setEmail] = useState(queryEmail);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [lastRequests, setLastRequests] = useLocalStorage('jury_otp_last_requests', {});
    const [countdown, setCountdown] = useState(0);

    const reset = useJuryStore((s) => s.reset);

    const updateCountdown = () => {
        if (!email) {
            setCountdown(0);
            return;
        }
        const lastRequest = lastRequests[email] || 0;
        const now = Date.now();
        const elapsed = Math.floor((now - lastRequest) / 1000);
        const remaining = Math.max(0, 60 - elapsed);
        setCountdown(remaining);
    };

    // Update countdown every second if needed
    useInterval(updateCountdown, countdown > 0 ? 1000 : null);

    // Also update immediately when email or lastRequests changes.
    // updateCountdown is intentionally omitted from deps.
    useEffect(() => {
        updateCountdown();
    }, [email, lastRequests]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOTPRequested = async (submittedEmail) => {
        setLoading(true);
        setError('');
        // Set email early so countdown can be calculated for this email if it was already throttled
        setEmail(submittedEmail);

        try {
            await axios.post('/jury/requestOtp', { email: submittedEmail });
            setLastRequests((prev) => ({ ...prev, [submittedEmail]: Date.now() }));
            setStep('verify');
        } catch (err) {
            const status = err.response?.status;
            if (status === 429) {
                setError(err.response.data?.message || 'Zu viele Anfragen. Bitte warten Sie eine Minute.');
                setLastRequests((prev) => ({ ...prev, [submittedEmail]: Date.now() }));
            } else if (status === 401) {
                setError('Diese E-Mail-Adresse ist nicht als Jurymitglied registriert.');
            } else {
                setError('Das Einmalpasswort konnte nicht gesendet werden. Bitte versuchen Sie es erneut.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOTPVerified = async (otp) => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.post('/jury/auth', { email, otp });
            tokenStorage.setAccessToken(res.data.accessToken);
            // Reset store so fresh data is loaded for this session
            reset();
            history.push('/jury/rahmenbedingungen');
        } catch (err) {
            const status = err.response?.status;
            if (status === 401) {
                setError(err.response.data?.message || 'Dieses Einmalpasswort ist ungültig oder abgelaufen.');
            } else {
                setError('Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setLoading(true);
        setError('');
        try {
            await axios.post('/jury/requestOtp', { email });
            setLastRequests((prev) => ({ ...prev, [email]: Date.now() }));
        } catch (err) {
            const status = err.response?.status;
            if (status === 429) {
                setError(err.response.data?.message || 'Zu viele Anfragen. Bitte warten Sie eine Minute.');
                setLastRequests((prev) => ({ ...prev, [email]: Date.now() }));
            } else {
                setError('Das Einmalpasswort konnte nicht erneut gesendet werden. Bitte versuchen Sie es erneut.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBackToRequest = () => {
        setStep('request');
        // We keep the email in state so if they go back and re-enter the same email,
        // the countdown is still active based on the persisted timestamp.
        setError('');
    };

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col xs={12} sm={12} md={9} lg={7} xl={6}>
                    {error && (
                        <div className="alert alert-danger text-center mb-4" role="alert">
                            {error}
                        </div>
                    )}
                    {step === 'request' ? (
                        <RequestOTP
                            onOTPRequested={handleOTPRequested}
                            loading={loading}
                            countdown={countdown}
                            initialEmail={email}
                            onEmailChange={(e) => setEmail(e)}
                        />
                    ) : (
                        <VerifyOTP
                            email={email}
                            onOTPVerified={handleOTPVerified}
                            onResendCode={handleResendOTP}
                            onBackToRequest={handleBackToRequest}
                            loading={loading}
                            countdown={countdown}
                        />
                    )}
                </Col>
            </Row>
            <Row className="justify-content-center">
                <Col className="text-center mt-5 pt-4">
                    <p className="mb-1">
                        Bei Fragen melden Sie sich gerne bei uns
                        <br />
                        Social Value GmbH
                        <br />
                        c/o Sebastian Matheis
                        <br />
                        Merheimer Str. 81, 50733 Köln
                    </p>
                    <p className="mb-0">
                        <a
                            href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'hilfe@spendenwettbewerb.de'}`}
                            className="text-primary"
                        >
                            {process.env.REACT_APP_SUPPORT_EMAIL || 'hilfe@spendenwettbewerb.de'}
                        </a>
                    </p>
                    <p className="mb-0">
                        <a href="tel:+4922167774683" className="text-primary">
                            0221 677 746 83
                        </a>
                    </p>
                </Col>
            </Row>
        </Container>
    );
}

export default JuryAuth;
